import React, { useCallback, useState } from "react";
import { Collapse, type CollapseProps } from "antd";
import { toast } from "react-toastify";
import AddonList from "../../../components/Settings/Subscription/Addons";
import SubscribedAddonList from "../../../components/Settings/Subscription/SubscribedAddons";
import PaymentCard from "../../../components/Settings/Subscription/PaymentCard";
import BillingHistory from "../../../components/Settings/Subscription/BillingHistory";
import CancelledNumbersList from "../../../components/Settings/Subscription/CancelledNumber";
import ViewCard from "../../../components/Settings/Subscription/PaymentCard/ViewCard";
import TopUpScreen from "../../Common/TopUpScreen";
import CallingAddons from "../../../components/Settings/Subscription/Addons/CallAddons";
import SMSAddons from "../../../components/Settings/Subscription/Addons/SMSAddon";
import BuyNumbers from "../BuyNumber";
import SettingsSidebarHeader from "../../../components/common/SettingsSidebarHeader";
import ENDPOINTS from "../../../services/endpoint";
import apiInstance from "../../../services/apiService";
import HomePhone from "../../../components/Settings/Subscription/HomePhone";
import { HomePhoneProduct } from "../../../types/homePhone";
import "./index.scss";

interface SubscriptionProps {
  handleBackToPrevious: () => void;
  showOnlySubAddon?: boolean;
  phoneNumberToDelete?: string;
  selectedHomePhoneProductId?: string;
  onHomePhoneProductSelect?: (product: HomePhoneProduct) => void;
}

interface CancelledNumber {
  _id: string;
  cost: string;
  currency: string;
  phoneNumber: string;
  remainingDays: number;
}

const getErrorMessage = (error: unknown, fallback: string) => {
  return error instanceof Error ? error.message : fallback;
};

const Subscription: React.FC<SubscriptionProps> = ({
  handleBackToPrevious,
  showOnlySubAddon = false,
  phoneNumberToDelete,
  selectedHomePhoneProductId,
  onHomePhoneProductSelect,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [cancelledNumberLoading, setCancelledNumbersLoading] =
    useState<boolean>(true);
  const [addonData, setAddonData] = useState<unknown>();
  const [currentScreen, setCurrentScreen] = useState("subscription");
  const [subscribedAddonBillingPrice, setSubscribedAddonBillingPrice] =
    useState();
  const [paymentCardId, setPaymentCardId] = useState<string>();
  const [activeKey, setActiveKey] = useState<string | string[]>(
    showOnlySubAddon ? ["2"] : [],
  );
  const [cancelledNumbers, setCancelledNumbers] = useState<CancelledNumber[]>(
    [],
  );

  const getAddonsData = useCallback(async () => {
    const url = ENDPOINTS.SUBSCRIBED_ADDON_DETAILS;

    setLoading(true);
    try {
      const response = await apiInstance.get(url);
      const data = response.data.data;
      setAddonData(data);
    } catch (error: unknown) {
      console.error(error);
      toast.error(
        getErrorMessage(error, "There was an error. Please try again"),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const getCancelledNumbers = useCallback(async () => {
    const url = ENDPOINTS.GET_CANCELLED_NUMBERS;

    setCancelledNumbersLoading(true);
    try {
      const response = await apiInstance.get(url);
      const data = response.data.data;
      setCancelledNumbers(data);
    } catch (error: unknown) {
      console.error(error);
      toast.error(
        getErrorMessage(error, "There was an error. Please try again"),
      );
    } finally {
      setCancelledNumbersLoading(false);
    }
  }, []);

  const allSubscriptionSections = [
    {
      id: "1",
      name: "Addons",
      component: <AddonList setCurrentScreen={setCurrentScreen} />,
    },
    {
      id: "2",
      name: "Subscribed Add-on",
      endTitle: subscribedAddonBillingPrice,
      component: (
        <SubscribedAddonList
          loading={loading}
          setLoading={setLoading}
          addonData={addonData}
          getAddonsData={getAddonsData}
          setSubscribedAddonBillingPrice={setSubscribedAddonBillingPrice}
          refreshCancelledNumbers={getCancelledNumbers}
        />
      ),
    },
    {
      id: "3",
      name: "Payment Card",
      component: (
        <PaymentCard
          handleViewCard={() => setCurrentScreen("view-card")}
          setPaymentCardId={setPaymentCardId}
        />
      ),
    },

    {
      id: "4",
      name: "Billing Detail",
      component: <BillingHistory />,
    },
    {
      id: "5",
      name: "Cancelled Number",
      component: (
        <CancelledNumbersList
          refreshSubbedAddons={getAddonsData}
          getCancelledNumbers={getCancelledNumbers}
          cancelledNumbers={cancelledNumbers}
          loading={cancelledNumberLoading}
          setLoading={setCancelledNumbersLoading}
        />
      ),
    },
    {
      id: "6",
      name: "HomePhone",
      component: (
        <HomePhone
          selectedProductId={selectedHomePhoneProductId}
          onProductSelect={(product) => {
            onHomePhoneProductSelect?.(product);
          }}
        />
      ),
    },
  ];

  const subscribedAddonsOnly = [
    {
      id: "2",
      name: "Subscribed Add-on",
      endTitle: subscribedAddonBillingPrice,
      component: (
        <SubscribedAddonList
          loading={loading}
          setLoading={setLoading}
          addonData={addonData}
          getAddonsData={getAddonsData}
          setSubscribedAddonBillingPrice={setSubscribedAddonBillingPrice}
          showOnlyPhoneNumbers={true}
          phoneNumberToDelete={phoneNumberToDelete}
          refreshCancelledNumbers={getCancelledNumbers}
        />
      ),
    },
  ];

  const customHeader = (title: string, end?: React.ReactNode) => (
    <div className="custom-header">
      <span>{title}</span>
      <span className="end-text">{end}</span>
    </div>
  );

  const subscriptionSections = showOnlySubAddon
    ? subscribedAddonsOnly
    : allSubscriptionSections;

  const collapseItems: CollapseProps["items"] = subscriptionSections.map(
    (item) => ({
      key: item.id,
      label: customHeader(item.name, item.endTitle),
      children: item.component,
    }),
  );

  const handleBackToSubScription = () => {
    setCurrentScreen("subscription");
  };

  return (
    <>
      {currentScreen === "subscription" && (
        <div className="subscription-sider">
          <SettingsSidebarHeader
            title="Subscription"
            handleBackClick={handleBackToPrevious}
          />

          <div className="options-container">
            <Collapse
              {...(showOnlySubAddon ? { activeKey } : {})}
              onChange={(key) => setActiveKey(key)}
              expandIconPosition="end"
              className="custom-collapse subscription-collapse"
              activeKey={activeKey}
              items={collapseItems}
            />
          </div>
        </div>
      )}

      {currentScreen === "view-card" && (
        <ViewCard
          handleBackToPrevious={handleBackToSubScription}
          id={paymentCardId}
        />
      )}

      {currentScreen === "top-up" && (
        <TopUpScreen handleBackToPrevious={handleBackToSubScription} />
      )}

      {currentScreen === "call-addons" && (
        <CallingAddons handleBackToPrevious={handleBackToSubScription} />
      )}

      {currentScreen === "purchase-number" && (
        <div className="h-full">
          <BuyNumbers handleBackToPrevious={handleBackToSubScription} />
        </div>
      )}

      {currentScreen === "sms-addons" && (
        <SMSAddons handleBackToPrevious={handleBackToSubScription} />
      )}
    </>
  );
};

export default Subscription;
